
import { createAsyncThunk, createSlice } from "@reduxjs/toolkit";
import SubDivisionService from "../../services/subdivision.service";

// ---------------------------------------------
// COMMON HELPERS
// ---------------------------------------------

const getErrorMessage = (error) => {
  return (
    error.response?.data?.message ||
    error.message ||
    "Something went wrong"
  );
};

const extractSubDivisions = (data) => {
  if (Array.isArray(data)) return data;

  return (
    data?.subDivisions ||
    data?.subdivisions ||
    data?.data?.subDivisions ||
    data?.data?.subdivisions ||
    []
  );
};

const extractSubDivision = (data) => {
  return (
    data?.subDivision ||
    data?.subdivision ||
    data?.data?.subDivision ||
    data?.data?.subdivision ||
    data
  );
};

// ---------------------------------------------
// GET ALL SUBDIVISIONS
// ---------------------------------------------

export const getSubDivisions = createAsyncThunk(
  "subdivision/getSubDivisions",
  async (params = {}, thunkAPI) => {
    try {
      const response = await SubDivisionService.getSubDivisions(params);

      if (response?.status !== 1) {
        return thunkAPI.rejectWithValue(
          response?.message || "Failed to fetch subdivisions"
        );
      }

      return response.data;
    } catch (error) {
      return thunkAPI.rejectWithValue(getErrorMessage(error));
    }
  }
);

// ---------------------------------------------
// GET SUBDIVISION BY ID
// ---------------------------------------------

export const getSubDivisionById = createAsyncThunk(
  "subdivision/getSubDivisionById",
  async (id, thunkAPI) => {
    try {
      const response = await SubDivisionService.getSubDivisionById(id);

      if (response?.status !== 1) {
        return thunkAPI.rejectWithValue(
          response?.message || "Failed to fetch subdivision details"
        );
      }

      return response.data;
    } catch (error) {
      return thunkAPI.rejectWithValue(getErrorMessage(error));
    }
  }
);

// ---------------------------------------------
// BULK CREATE SUBDIVISIONS
// ---------------------------------------------

export const bulkCreateSubDivision = createAsyncThunk(
  "subdivision/bulkCreateSubDivision",
  async (payload, { rejectWithValue }) => {
    try {
      const response =
        await SubDivisionService.bulkCreateSubDivision(payload);

      if (response?.status !== 1) {
        return rejectWithValue(
          response?.message || "Failed to bulk create subdivisions"
        );
      }

      return response;
    } catch (error) {
      return rejectWithValue(getErrorMessage(error));
    }
  }
);

// ---------------------------------------------
// CREATE SUBDIVISION
// ---------------------------------------------

export const createSubDivision = createAsyncThunk(
  "subdivision/createSubDivision",
  async (payload, thunkAPI) => {
    try {
      const response = await SubDivisionService.createSubDivision(payload);

      if (response?.status !== 1) {
        return thunkAPI.rejectWithValue(
          response?.message || "Failed to create subdivision"
        );
      }

      return response;
    } catch (error) {
      return thunkAPI.rejectWithValue(getErrorMessage(error));
    }
  }
);

// ---------------------------------------------
// UPDATE SUBDIVISION
// ---------------------------------------------

export const updateSubDivision = createAsyncThunk(
  "subdivision/updateSubDivision",
  async ({ id, payload }, thunkAPI) => {
    try {
      const response = await SubDivisionService.updateSubDivision(
        id,
        payload
      );

      if (response?.status !== 1) {
        return thunkAPI.rejectWithValue(
          response?.message || "Failed to update subdivision"
        );
      }

      return response;
    } catch (error) {
      return thunkAPI.rejectWithValue(getErrorMessage(error));
    }
  }
);

// ---------------------------------------------
// DELETE SUBDIVISION
// ---------------------------------------------

export const deleteSubDivision = createAsyncThunk(
  "subdivision/deleteSubDivision",
  async (id, thunkAPI) => {
    try {
      const response = await SubDivisionService.deleteSubDivision(id);

      if (response?.status !== 1) {
        return thunkAPI.rejectWithValue(
          response?.message || "Failed to delete subdivision"
        );
      }

      return id;
    } catch (error) {
      return thunkAPI.rejectWithValue(getErrorMessage(error));
    }
  }
);

// ---------------------------------------------
// INITIAL STATE
// ---------------------------------------------

const initialState = {
  subdivisions: [],
  selectedSubDivision: null,

  loading: false,
  detailsLoading: false,
  createLoading: false,
  bulkCreateLoading: false,
  updateLoading: false,
  deleteLoading: false,

  error: null,
  detailsError: null,
  successMessage: null,

  pagination: {
    total: 0,
    totalPages: 0,
    currentPage: 1,
    limit: 10
  },

  bulkResult: null
};

// ---------------------------------------------
// SLICE
// ---------------------------------------------

const subDivisionSlice = createSlice({
  name: "subdivision",
  initialState,

  reducers: {
    clearSubDivisionError: (state) => {
      state.error = null;
      state.detailsError = null;
    },

    clearSubDivisionMessage: (state) => {
      state.successMessage = null;
      state.bulkResult = null;
    },

    clearSelectedSubDivision: (state) => {
      state.selectedSubDivision = null;
    },

    clearBulkSubDivisionResult: (state) => {
      state.bulkResult = null;
    }
  },

  extraReducers: (builder) => {
    builder

      // -----------------------------------------
      // GET ALL SUBDIVISIONS
      // -----------------------------------------

      .addCase(getSubDivisions.pending, (state) => {
        state.loading = true;
        state.error = null;
      })

      .addCase(getSubDivisions.fulfilled, (state, action) => {
        state.loading = false;

        const data = action.payload || {};

        state.subdivisions = extractSubDivisions(data);

        state.pagination = {
          total: data?.pagination?.total ?? data?.total ?? state.subdivisions.length,
          currentPage:
            data?.pagination?.currentPage ?? data?.currentPage ?? 1,
          totalPages:
            data?.pagination?.totalPages ?? data?.totalPages ?? 0,
          limit: data?.pagination?.limit ?? data?.limit ?? 10
        };
      })

      .addCase(getSubDivisions.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload || action.error.message;
      })

      // -----------------------------------------
      // GET SUBDIVISION BY ID
      // -----------------------------------------

      .addCase(getSubDivisionById.pending, (state) => {
        state.detailsLoading = true;
        state.detailsError = null;
      })

      .addCase(getSubDivisionById.fulfilled, (state, action) => {
        state.detailsLoading = false;
        state.selectedSubDivision = extractSubDivision(action.payload);
      })

      .addCase(getSubDivisionById.rejected, (state, action) => {
        state.detailsLoading = false;
        state.detailsError = action.payload || action.error.message;
      })

      // -----------------------------------------
      // CREATE SUBDIVISION
      // -----------------------------------------

      .addCase(createSubDivision.pending, (state) => {
        state.createLoading = true;
        state.error = null;
        state.successMessage = null;
      })

      .addCase(createSubDivision.fulfilled, (state, action) => {
        state.createLoading = false;

        state.successMessage =
          action.payload?.message || "SubDivision created successfully";

        const subdivision = extractSubDivision(action.payload?.data);

        if (subdivision?._id) {
          state.subdivisions.unshift(subdivision);
          state.pagination.total += 1;
        }
      })

      .addCase(createSubDivision.rejected, (state, action) => {
        state.createLoading = false;
        state.error = action.payload || action.error.message;
      })

      // -----------------------------------------
      // BULK CREATE SUBDIVISIONS
      // -----------------------------------------

      .addCase(bulkCreateSubDivision.pending, (state) => {
        state.bulkCreateLoading = true;
        state.error = null;
        state.successMessage = null;
        state.bulkResult = null;
      })

      .addCase(bulkCreateSubDivision.fulfilled, (state, action) => {
        state.bulkCreateLoading = false;

        const data = action.payload?.data || {};

        const createdSubDivisions = extractSubDivisions(data);

        state.bulkResult = data;

        state.successMessage =
          action.payload?.message ||
          "SubDivisions imported successfully";

        if (createdSubDivisions.length > 0) {
          const existingIds = new Set(
            state.subdivisions.map((item) => String(item._id))
          );

          const newItems = createdSubDivisions.filter(
            (item) => item?._id && !existingIds.has(String(item._id))
          );

          state.subdivisions = [
            ...newItems,
            ...state.subdivisions
          ];

          state.pagination.total += newItems.length;
        }
      })

      .addCase(bulkCreateSubDivision.rejected, (state, action) => {
        state.bulkCreateLoading = false;
        state.error = action.payload || action.error.message;
      })

      // -----------------------------------------
      // UPDATE SUBDIVISION
      // -----------------------------------------

      .addCase(updateSubDivision.pending, (state) => {
        state.updateLoading = true;
        state.error = null;
        state.successMessage = null;
      })

      .addCase(updateSubDivision.fulfilled, (state, action) => {
        state.updateLoading = false;

        state.successMessage =
          action.payload?.message || "SubDivision updated successfully";

        const updatedSubDivision = extractSubDivision(
          action.payload?.data
        );

        if (updatedSubDivision?._id) {
          const index = state.subdivisions.findIndex(
            (subdivision) =>
              String(subdivision._id) === String(updatedSubDivision._id)
          );

          if (index !== -1) {
            state.subdivisions[index] = updatedSubDivision;
          }

          if (
            String(state.selectedSubDivision?._id) ===
            String(updatedSubDivision._id)
          ) {
            state.selectedSubDivision = updatedSubDivision;
          }
        }
      })

      .addCase(updateSubDivision.rejected, (state, action) => {
        state.updateLoading = false;
        state.error = action.payload || action.error.message;
      })

      // -----------------------------------------
      // DELETE SUBDIVISION
      // -----------------------------------------

      .addCase(deleteSubDivision.pending, (state) => {
        state.deleteLoading = true;
        state.error = null;
        state.successMessage = null;
      })

      .addCase(deleteSubDivision.fulfilled, (state, action) => {
        state.deleteLoading = false;

        state.subdivisions = state.subdivisions.filter(
          (subdivision) =>
            String(subdivision._id) !== String(action.payload)
        );

        if (
          String(state.selectedSubDivision?._id) ===
          String(action.payload)
        ) {
          state.selectedSubDivision = null;
        }

        state.pagination.total = Math.max(
          0,
          state.pagination.total - 1
        );

        state.successMessage = "SubDivision deleted successfully";
      })

      .addCase(deleteSubDivision.rejected, (state, action) => {
        state.deleteLoading = false;
        state.error = action.payload || action.error.message;
      });
  }
});

// ---------------------------------------------
// EXPORTS
// ---------------------------------------------

export const {
  clearSubDivisionError,
  clearSubDivisionMessage,
  clearSelectedSubDivision,
  clearBulkSubDivisionResult
} = subDivisionSlice.actions;

export default subDivisionSlice.reducer;
